// Live count: how many people have the site open right now, from the worker in worker/ (README "Live count").
// Only loficities.com reports in: deploy previews, the headless tools (file://) and OBS sources (often left running
// for days) stay out of the count. On localhost, ?live connects to a local `pnpm live:dev` worker instead.
// The same socket carries Together (README "Together", protocol v2): floating reactions and shared lanterns, the
// chat (README "Chat", site/js/live/chat.js), and who listens to the radio (docs/APP_REFERENCE.md "Radio",
// site/js/audio/radio.js).

const SITE = 'wss://live.loficities.com';
const DEV = 'ws://localhost:8787';
const PING_MS = 30000;      // the worker drops pages that stop pinging (STALE_MS in worker/index.js)
const KEEP_MS = 15000;      // keep showing the last count this long while reconnecting
const GIVE_UP = 4;          // failed tries before this visit stops trying, unless one got through
export const TOO_MANY = 4008;   // the worker closed this page for going past its limits (CLOSE_TOO_MANY in worker/social.js):
const SLOW_DOWN_MS = 60000;     // come back in a minute, not in a second or two
export const SWITCHED_OFF = 4009;   // the live features are switched off for now (CLOSE_OFF in worker/switches.js):
export const OFF_WAIT_MS = 30 * 60000;   // look again in 15 to 30 min, so open tabs come back on their own, not all at once
const OFFSETS = 8;              // clock samples serverNow() picks from

// The worker's limits (worker/social.js), mirrored so the page can say no before it asks.
export const REACTIONS = ['heart', 'note', 'star', 'moon'];
export const REACT_CAP = 5;                 // a burst of reactions,
export const REACT_EVERY_MS = 700;          // then one more every 0.7 s
export const LANTERN_LIFE_MS = 75000;       // a lantern's flight
export const LANTERN_CITY_MAX = 12;         // in flight at once in one city
export const LANTERN_EVERY_MS = 15000;      // one per page per 15 s
export const LANTERN_X_MIN = 8, LANTERN_X_MAX = 471;   // the scene columns a lantern can start from

/** The worker this page reports to, or null for a page that doesn't count. */
export function liveUrl(loc, obs) {
  if (obs) return null;
  if (loc.hostname === 'loficities.com') return SITE;
  const local = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(loc.hostname);
  return local && new URLSearchParams(loc.search).has('live') ? DEV : null;
}

const n = x => x.toLocaleString('en-US');

// People who came by before the worker counted "all time": PostHog's visitors to loficities.com from the launch
// (2026-09-25) who weren't back in the day before the worker started counting (2026-09-27 21:36:24 UTC), when it knew
// everyone from that day (README "Live count"). Visitors PostHog never sees (ad blockers, Do Not Track) are missing
// from it, so it's low.
export const ALL_BEFORE = 2329;

/** Everyone who ever came by: the worker's count and ALL_BEFORE, never fewer than today; null from a worker without it. */
export function allTime(counts) {
  if (!Number.isFinite(counts?.all) || counts.all < 0) return null;
  return Math.max(ALL_BEFORE + counts.all, counts.today ?? 0, counts.total ?? 0);
}

/**
 * How many people are in each city right now, e.g. { tokyo: 5, paris: 1 }, for the pill and the city picker's cards.
 * You're in the city you see (cityId), even before the worker has heard about the switch; no city has more people than
 * the site, and a city nobody is in is left out. Empty when there's nobody to mention (only you) or no count yet.
 */
export function cityCounts(counts, cityId) {
  const total = counts?.total ?? 0, here = {};
  if (total < 2) return here;
  for (const [id, k] of Object.entries(counts.cities ?? {})) if (Number.isFinite(k) && k >= 1) here[id] = Math.min(total, k);
  if (cityId) here[cityId] = Math.max(1, here[cityId] ?? 0);
  return here;
}

/** The city picker's cards, e.g. { tokyo: '5 here', paris: 'just you' } (you, alone in the city you see). */
export function cityLabels(counts, cityId) {
  const labels = {};
  for (const [id, k] of Object.entries(cityCounts(counts, cityId))) labels[id] = id === cityId && k === 1 ? 'just you' : `${n(k)} here`;
  return labels;
}

/**
 * The city with the most people right now, for the city picker's featured card, or null when no city has two or more
 * (only you, or everyone in a city of their own). `order` lists the cities it picks from (the picker's) and settles a
 * tie, unless `keep` (the city featured so far) is one of them: the card stays put while two cities draw level.
 */
export function topCity(counts, cityId, order, keep = null) {
  const here = cityCounts(counts, cityId);
  let top = null;
  for (const id of order) if ((here[id] ?? 0) >= 2 && (top === null || here[id] > here[top])) top = id;
  return top !== null && order.includes(keep) && here[keep] === here[top] ? keep : top;
}

/**
 * The city picker's order: the cities with the most people first (cityCounts, you included), then the ones nobody is
 * in. A tie, and every city while there's no count, keeps its place in `order`.
 */
export function byPeople(counts, cityId, order) {
  const here = cityCounts(counts, cityId);
  return [...order].sort((a, b) => (here[b] ?? 0) - (here[a] ?? 0));
}

/**
 * The pill's parts, e.g. { now: '23 here now', city: '5 in Tokyo', today: '480 today', all: '12,345 all time' }, read
 * "23 here now · 5 in Tokyo · 480 today · 12,345 all time". Alone it's { today: '480 here today', all: … } (the other
 * visitors), and null when there's nobody to mention (only you, ever) or no count yet. A part that says nothing new is
 * left out (null).
 */
export function liveParts(counts, cityId, cityName) {
  const total = counts?.total ?? 0;
  const today = Math.max(counts?.today ?? 0, total);   // a worker from before "today" sends none
  const ever = allTime(counts);                          // nor one from before "all time"
  const all = ever !== null && ever > today ? `${n(ever)} all time` : null;
  if (total < 2) return today < 2 && !all ? null : { now: null, city: null, today: today < 2 ? null : `${n(today)} here today`, all };
  let city = null;
  if (cityId) {
    const here = cityCounts(counts, cityId)[cityId];
    city = here === total ? `${total === 2 ? 'both' : 'all'} in ${cityName}`
      : here === 1 ? `just you in ${cityName}`
      : `${n(here)} in ${cityName}`;
  }
  return { now: `${n(total)} here now`, city, today: today > total ? `${n(today)} today` : null, all };
}

// Epoch ms that never jumps (Date.now() follows clock corrections): the base the worker's clock is kept against.
const monotonic = () => performance.timeOrigin + performance.now();

/** When this browser's day began: its local midnight (epoch ms), where the pill's "today" counts from. */
export const localMidnight = (at = Date.now()) => new Date(at).setHours(0, 0, 0, 0);

/**
 * Keeps a WebSocket to the worker: it connects on the first setCity(), reports city switches, reconnects after drops
 * and calls onChange({ total, cities: { tokyo: 5, … }, today }) on every count, or onChange(null) once it has none.
 * `id` is this browser's random id, so its tabs count as one person. A `fresh` one (new, or not yet heard counted)
 * connects without `back`, so the worker counts it in "all time" if it doesn't know it; onCounted() says once when a
 * worker that counts "all time" answered, and from then on the page says `back`. Optional onStatus reports the first
 * valid count and the first unavailable state per page, without browser IDs or a report on every heartbeat.
 *
 * Together (protocol v2): on(type, cb) → off hears the worker's typed messages ('react', 'lantern', 'lanterns',
 * 'denied', and for the chat 'chat', 'msg' and 'hush') and the connection's own 'open' (the worker's first typed
 * message: the city's lanterns, sent on connect, or what's switched off, so an older worker never gets a message it
 * would misread) and
 * 'close'. The page asks for the chat with `chat=1`; a worker from before the chat never sends 'chat', so the page
 * knows not to offer it. The page also asks for the switches (`switches=1`): the worker says what's on ('switches':
 * { live, chat, together, jobStrip, billboardJobs, billboardSetting, billboards, chatPosts, salePlane }) on connect and whenever the admin page changes it, and with the
 * live features all off it closes with SWITCHED_OFF, so the counts go at once and the page looks again in a while.
 * send(obj) → false unless connected. serverNow() is the worker's clock, from the `now` its
 * lantern, chat and radio messages carry (Date.now() before the first one): a message only ever arrives late, so of the
 * last OFFSETS samples the least late (the largest offset) is the closest.
 * The radio: every page says it knows it (`radio`, 1 while it listens) and setRadio(on) says when that changes; the
 * worker answers with 'radio' messages ({ n: how many listen, now }) on connect and with every update, and for its
 * vote 'picks' (on connect: the winners so far and the open rounds' tallies), 'votes' (tallies) and 'pick' (a winner).
 * `now` is the local clock and `day` gives the local midnight (tests). The worker hears the day with the connection
 * and again after midnight (checked with every ping), so "today" starts again at midnight where the visitor is.
 */
export function createLive(url, { id, fresh = false, onCounted = () => {}, onChange, onStatus = () => {}, now = monotonic, day = localMidnight }) {
  let ws = null, city = null, sent = null, sentDay = null, started = false, stopped = false, reached = false, tries = 0;
  let ping = 0, retry = 0, keep = 0, v2 = false, radio = false, sentRadio = false, off = false, swap = false;
  const offsets = [];
  const sample = at => { offsets.push(at - now()); if (offsets.length > OFFSETS) offsets.shift(); };
  const reported = new Set();
  function report(status, props) {
    if (stopped || reported.has(status)) return;
    reported.add(status);
    onStatus(status, props);
  }
  const listeners = new Map();
  const emit = (type, m) => {
    for (const cb of [...(listeners.get(type) ?? [])]) { try { cb(m); } catch (e) { console.error(e); } }
  };
  const open = () => v2 && ws?.readyState === WebSocket.OPEN;

  function connect() {
    retry = 0;
    if (stopped || ws) return;
    off = false;
    const u = new URL(url);
    u.searchParams.set('id', id);
    if (city) u.searchParams.set('city', city);
    u.searchParams.set('v', '2');
    u.searchParams.set('chat', '1');
    u.searchParams.set('switches', '1');
    u.searchParams.set('day', sentDay = day());
    u.searchParams.set('radio', radio ? '1' : '0');
    sentRadio = radio;
    if (!fresh) u.searchParams.set('back', '1');
    sent = city;
    try { ws = new WebSocket(u); } catch {
      report('unavailable', { reason: 'socket_refused' });
      return;                                       // e.g. mixed content: the page carries on
    }
    ws.onopen = () => {
      reached = true;
      if (city !== sent) sendCity();                  // switched while connecting
      if (radio !== sentRadio) sendRadio();
      ping = setInterval(() => {
        if (ws?.readyState !== WebSocket.OPEN) return;
        if (day() !== sentDay) ws.send(JSON.stringify({ day: sentDay = day() }));   // an older worker ignores it
        ws.send('ping');
      }, PING_MS);
    };
    ws.onmessage = e => {
      if (e.data === 'pong') return;
      let m;
      try { m = JSON.parse(e.data); } catch { return; }
      if (!m || typeof m !== 'object' || Array.isArray(m)) return;
      if (typeof m.t !== 'string') {                  // untyped: the counts
        if (!Number.isFinite(m.total) || m.total < 0) return;
        tries = 0;                                 // an open socket alone doesn't prove the worker recovered
        clearTimeout(keep);
        keep = 0;
        if (fresh && Number.isFinite(m.all)) { fresh = false; onCounted(); }   // counted as it connected
        const ever = allTime(m);
        report('loaded', { visitors_now: m.total, visitors_today: Math.max(m.today ?? m.total, m.total), ...(ever !== null && { visitors_all: ever }) });
        onChange(m);
        return;
      }
      // The worker's clock as of this message: off by the one-way delay, well under a pixel of a lantern's flight.
      if (['lantern', 'lanterns', 'chat', 'radio', 'picks', 'pick', 'votes'].includes(m.t) && Number.isFinite(m.now)) sample(m.now);
      if (m.t === 'switches' && m.live === false) {   // all switched off: the count goes now, and the close that follows
        off = true;                                   // is the switch, whatever code it ends with and however long it takes
        clearTimeout(keep);
        keep = 0;
        onChange(null);
      }
      if (!v2) { v2 = true; emit('open'); }
      emit(m.t, m);
    };
    ws.onclose = e => {
      clearInterval(ping);
      const was = v2;
      v2 = false;
      ws = null;
      if (was) emit('close');
      if (stopped) return;                          // leaving the page is not an outage
      if (swap) { swap = false; connect(); return; }   // another id (setId): back at once, as it
      if (off || e?.code === SWITCHED_OFF) {        // not an outage either: no count to keep, and no hurry
        off = false;
        clearTimeout(keep);
        keep = 0;
        onChange(null);
        report('unavailable', { reason: 'switched_off' });
        retry = setTimeout(connect, OFF_WAIT_MS * (0.5 + Math.random() / 2));
        return;
      }
      // One deadline per outage: failed retries must not keep stale counts alive. Keep the handle after expiry
      // so further failures don't emit null again; a valid count (or an intentional exit) clears it.
      if (!keep) keep = setTimeout(() => {
        onChange(null);
        report('unavailable', { reason: reached ? 'connection_lost' : 'connection_failed' });
      }, KEEP_MS);
      if (!reached && ++tries >= GIVE_UP) return;    // blocked, or no worker there: not this visit
      if (reached) tries++;
      const wait = e?.code === TOO_MANY ? SLOW_DOWN_MS : Math.min(60000, 1000 * 2 ** tries) * (0.5 + Math.random() / 2);
      retry = setTimeout(connect, wait);
    };
  }
  function sendCity() {
    ws.send(JSON.stringify({ city }));
    sent = city;
  }
  function sendRadio() {
    ws.send(JSON.stringify({ radio }));
    sentRadio = radio;
  }

  addEventListener('online', () => { if (retry) { clearTimeout(retry); connect(); } });
  // Leaving the page says goodbye at once, and lets the browser keep it in its back/forward cache.
  addEventListener('pagehide', () => { stopped = true; clearTimeout(retry); clearTimeout(keep); keep = 0; ws?.close(1000); });
  addEventListener('pageshow', e => { if (e.persisted && started) { stopped = false; connect(); } });

  return {
    setCity(next) {
      if (next === city) return;
      city = next;
      if (!started) { started = true; connect(); }
      else if (ws?.readyState === WebSocket.OPEN) sendCity();
    },
    /**
     * This browser's id is another from now on (signing in takes the account's, signing out a new one): the socket
     * connects again with it, `fresh` if no worker counted it yet. The chat says who this page is as it connects.
     */
    setId(next, isFresh = false) {
      if (next === id) return;
      id = next;
      fresh = isFresh;
      if (!ws) return;                                // the next connect has it
      clearTimeout(retry);
      swap = true;
      ws.close(1000);
    },
    /** This page started or stopped listening to the radio (a worker from before the radio ignores it). */
    setRadio(on) {
      radio = !!on;
      if (radio !== sentRadio && ws?.readyState === WebSocket.OPEN) sendRadio();
    },
    send(obj) {
      if (!open()) return false;
      try { ws.send(JSON.stringify(obj)); return true; } catch { return false; }
    },
    on(type, cb) {
      if (!listeners.has(type)) listeners.set(type, new Set());
      listeners.get(type).add(cb);
      return () => listeners.get(type).delete(cb);
    },
    serverNow: () => (offsets.length ? now() + Math.max(...offsets) : Date.now()),
    get connected() { return open(); },
  };
}
